import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

export interface WizardStepEntityProps {
  accounts: Array<{ id: string; legalName: string; entityType: string }>;
  selectedAccountId?: string;
  onSelect: (accountId: string) => void;
  onNext: () => void;
  onCancel: () => void;
}

/**
 * Wizard Step 1: Entity Selection
 *
 * Shows dropdown of accounts (filtered by context):
 * - Client Group: Accounts in the client group
 * - Deal: Accounts linked to the deal
 *
 * Smart inference: if only one account, auto-select
 */
export const WizardStepEntity: React.FC<WizardStepEntityProps> = ({
  accounts,
  selectedAccountId,
  onSelect,
  onNext,
  onCancel,
}) => {
  // Auto-select if only one account
  React.useEffect(() => {
    if (accounts.length === 1 && !selectedAccountId) {
      onSelect(accounts[0].id);
    }
  }, [accounts, selectedAccountId, onSelect]);

  const isNextDisabled = !selectedAccountId;

  const getEntityTypeBadge = (entityType: string) => {
    const badgeVariants: Record<string, string> = {
      company: 'Trading',
      trust: 'Trust',
      partnership: 'Partnership',
      sole_trader: 'Individual',
    };
    return badgeVariants[entityType] || entityType;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold" data-testid="wizard-step-title">
          Entity Selection
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Select the entity for which you want to add financial data
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="entity-select">Entity / Account</Label>
        <Select value={selectedAccountId || 'none'} onValueChange={onSelect}>
          <SelectTrigger
            id="entity-select"
            data-testid="select-entity"
            className="w-full"
          >
            <SelectValue placeholder="Select an entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" disabled>
              Select an entity
            </SelectItem>
            {accounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                <div className="flex items-center gap-2">
                  <span>{account.legalName}</span>
                  <Badge variant="outline" className="text-xs">
                    {getEntityTypeBadge(account.entityType)}
                  </Badge>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {accounts.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No accounts available. Please create an account first.
          </p>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onNext} disabled={isNextDisabled} data-testid="button-wizard-next">
          Next
        </Button>
      </div>
    </div>
  );
};
